import React, { useCallback, useState } from "react";
import Theme from "@adsk/alloy-react-theme";
import Button from "@adsk/alloy-react-button";
import {
  Issue,
  IssueLinkedDocument,
  IssuePlacementWithPins,
  IssueType,
  MarkupCreationPayload,
  PlacementType,
} from "@adsk/acc-issues-types";
import { SHEET_SURFACE_TYPE } from "@adsk/acc-issues-core/state/redux/ducks/sheets";
import { useTranslations } from "@adsk/acc-issues-translations";
import { SheetAssociation } from "@adsk/acc-issues-types";
import { EditableFieldWrapper } from "@adsk/acc-issues-common/components/primitive/EditableFieldWrapper";
import PlacementDropdown, {
  PlacementOptions,
} from "../../../input-field-components/PlacementDropdown/PlacementDropdown";
import { PlacementDeleteModal } from "./PlacementDeleteModal";
import { trackRemoveIssueAnnotationSuccess } from "../../../utils/analytics/issueActionsTracking";
import { usePlacementHandlers } from "../hooks/PlacementFieldHooks/usePlacementHandlers";
import { XIcon } from "@adsk/alloy-react-icon";
import { PlacementDisplay } from "../PlacementDisplay";
import usePlacementData from "../hooks/PlacementFieldHooks/usePlacementData";
import { PlacementRevisionBadge } from "../../../input-field-components/PlacementRevisionBadge";
import styled from "styled-components";
import { CouldNotDisplayPlacement } from "@adsk/acc-issues-common/components/display/CouldNotDisplayPlacement";

type PlacementChangeFn = (
  {
    placements,
    linkedDocuments,
    snapshotBlob,
  }: {
    markup?: MarkupCreationPayload;
    placements?: IssuePlacementWithPins[];
    linkedDocuments?: IssueLinkedDocument[];
    snapshotBlob?: Blob;
  },
  handlers?: Partial<{
    onSuccess: any;
    onFailure: any;
  }>
) => void;

const PlacementWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const useDeleteConfirmation = ({
  sheetAssociation,
  onChange,
  issue,
  setIsLoading,
}: {
  onChange?: PlacementChangeFn;
  sheetAssociation?: SheetAssociation;
  issue: Issue;
  setIsLoading: (isLoading: boolean) => void;
}) => {
  const issueId = issue.id;
  const { onPlacementSuccess, onPlacementFailure } =
    usePlacementHandlers(setIsLoading);
  const [isPlacementDeleteModalOpen, setIsPlacementDeleteModalOpen] =
    useState(false);

  const handleDeleteConfirmation = useCallback(async () => {
    setIsLoading(true);

    trackRemoveIssueAnnotationSuccess({
      issue_uid: issueId,
      sheet_uid: sheetAssociation?.sheetId,
    });
    const isSheet = sheetAssociation?.surfaceType === SHEET_SURFACE_TYPE;
    try {
      onChange?.(
        {
          placements: [],
        },
        {
          onSuccess: () => onPlacementSuccess({ isSheet, isAdd: false }),
          onFailure: () => onPlacementFailure({ isSheet, isAdd: false }),
        }
      );
    } catch {
      onPlacementFailure({ isSheet, isAdd: false });
    }
    setIsPlacementDeleteModalOpen(false);
  }, [
    setIsLoading,
    sheetAssociation,
    issueId,
    onChange,
    onPlacementSuccess,
    onPlacementFailure,
  ]);

  return {
    isPlacementDeleteModalOpen,
    setIsPlacementDeleteModalOpen,
    handleDeleteConfirmation,
  };
};

export const PlacementField = ({
  issue,
  onChange,
  error,
  issueType,
  navigable = false,
  editable = true,
  unlinkable = true,
}: {
  issue: Issue;
  onChange?: PlacementChangeFn;
  error?: string;
  issueType?: IssueType;
  /** Placement is a clickable in-viewer deeplink (renders outside InlineEdit). */
  navigable?: boolean;
  /** Placement can be replaced inline (dropdown), subject to permission. */
  editable?: boolean;
  /** Placement can be removed (unlink "X"), subject to permission. */
  unlinkable?: boolean;
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { T } = useTranslations();
  const getPlacementData = usePlacementData();
  const {
    placementText,
    placementLink,
    document,
    sheetAssociation,
    hasPlacementError,
    isPlacementDeleted,
  } = getPlacementData(issue);

  const {
    isPlacementDeleteModalOpen,
    setIsPlacementDeleteModalOpen,
    handleDeleteConfirmation,
  } = useDeleteConfirmation({
    sheetAssociation,
    issue,
    onChange,
    setIsLoading,
  });

  const { onPlacementSuccess, onPlacementFailure } =
    usePlacementHandlers(setIsLoading);

  const userHasPermissionToEdit =
    issue.permittedAttributes.includes("linkedDocuments");
  const canEditPlacement = editable && userHasPermissionToEdit;
  const canUnlinkPlacement = unlinkable && userHasPermissionToEdit;

  const handleChange = useCallback(
    (
      placements: IssuePlacementWithPins[],
      options: PlacementOptions,
      placementType?: PlacementType
    ) => {
      setIsLoading(true);
      if (placements) {
        const isSheet = placementType === PlacementType.SheetView;
        onChange?.(
          {
            placements,
            // Do not send the locationId if placed outside a location or if placed in the same location as it was in.
            ...(options?.locationId &&
              options?.locationId != issue.locationId && {
                locationId: options.locationId,
              }),
            snapshotBlob: options?.snapshotBlob,
          },
          {
            onSuccess: () => {
              onPlacementSuccess({ isSheet, isAdd: true });
            },
            onFailure: () => {
              onPlacementFailure({ isSheet, isAdd: true });
            },
          }
        );
      } else {
        onChange?.({ placements: [] });
      }
    },
    [onChange, issue.locationId, onPlacementSuccess, onPlacementFailure]
  );

  const deletedText = isPlacementDeleted
    ? (T.translate("activityLog.fileWasDeleted") as string)
    : undefined;
  const placementValue = hasPlacementError
    ? (T.translate("activityLog.couldNotDisplay") as string)
    : deletedText ?? (placementText as string | undefined);

  const isPlacementFieldDisabled =
    hasPlacementError ||
    isPlacementDeleted ||
    !!placementText ||
    !canEditPlacement;

  const getFileLabel = useCallback(
    text => {
      return (
        <PlacementWrapper>
          <PlacementDisplay
            placementLink={placementLink}
            placementText={text}
            shouldShowLink={navigable}
            issue={issue}
            enableBreakWord={true}
          />
          <PlacementRevisionBadge issue={issue} />
        </PlacementWrapper>
      );
    },
    [issue, placementLink, navigable]
  );

  return (
    <>
      <div data-testid="placement-field">
        <EditableFieldWrapper
          loading={isLoading}
          fieldKey="placement"
          label={T.translate("issueAttributes.placement")}
          value={placementValue}
          renderValue={value => {
            if (hasPlacementError) {
              return <CouldNotDisplayPlacement />;
            }
            if (isPlacementDeleted) {
              return <span>{value}</span>;
            }
            if (!value) {
              return null;
            }
            return <div style={{ display: "flex" }}>{getFileLabel(value)}</div>;
          }}
          disabled={isPlacementFieldDisabled}
          showDisabledReason={
            editable && !userHasPermissionToEdit && !hasPlacementError
          }
          renderInput={({ onChange: inlineEditOnChange }) => {
            return (
              <PlacementDropdown
                onChange={handleChange}
                defaultIsOpen={true}
                onClose={inlineEditOnChange}
                error={!!error}
                issueType={issueType}
              />
            );
          }}
          renderTrailingElement={() => {
            if (
              (!placementText && !hasPlacementError && !isPlacementDeleted) ||
              !canUnlinkPlacement
            ) {
              return null;
            }
            return (
              <div data-testid="unlink-button">
                <Button
                  style={{
                    color: Theme.colors.charcoal900,
                  }}
                  renderIcon={p => <XIcon {...p} />}
                  variant={Button.VARIANTS.TERTIARY}
                  onClick={() => setIsPlacementDeleteModalOpen(true)}
                />
              </div>
            );
          }}
        />
      </div>
      <PlacementDeleteModal
        isOpen={isPlacementDeleteModalOpen}
        placementText={placementValue}
        isFile={!!document} //if !document then it is a sheet.
        onDelete={handleDeleteConfirmation}
        onClose={() => setIsPlacementDeleteModalOpen(false)}
      />
    </>
  );
};
